/* eslint-disable @typescript-eslint/no-unused-vars */
import React,{useState,useRef,useEffect} from 'react';
import api from '../../services/api';

interface Props {
  label?: string;
  system: 'icd10'|'snomed'|'loinc'|'icd11';
  value: string;
  onChange: (value: string, code?: string, display?: string) => void;
  placeholder?: string;
  className?: string;
  showCode?: boolean;
}

export default function TerminologySearch({label,system,value,onChange,placeholder,className='',showCode=true}:Props){
  const [query,setQuery]=useState(value||'');
  const [results,setResults]=useState<any[]>([]);
  const [loading,setLoading]=useState(false);
  const [open,setOpen]=useState(false);
  const [selected,setSelected]=useState<any>(null);
  const timer=useRef<any>(null);
  const ref=useRef<HTMLDivElement>(null);

  useEffect(()=>{
    const handler=(e:MouseEvent)=>{ if(ref.current&&!ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown',handler);
    return ()=>document.removeEventListener('mousedown',handler);
  },[]);

  const search=async(q:string)=>{
    if(!q||q.length<2){setResults([]);setOpen(false);return;}
    setLoading(true);
    try{
      let r:any;
      if(system==='icd10') r=await api.searchICD10?.(q);
      else if(system==='snomed') r=await api.searchSNOMED?.(q);
      else if(system==='loinc') r=await api.searchLOINC?.(q);
      else if(system==='icd11') r=await api.searchICD11?.(q);
      const list=Array.isArray(r?.data)?r.data:(r?.data?.results||r?.data?.data||[]);
      setResults(list);
      setOpen(list.length>0);
    }catch{setResults([]);}
    setLoading(false);
  };

  const handleChange=(e:React.ChangeEvent<HTMLInputElement>)=>{
    const q=e.target.value;
    setQuery(q);
    setSelected(null);
    onChange(q,'','');
    if(timer.current) clearTimeout(timer.current);
    timer.current=setTimeout(()=>search(q),400);
  };

  const pick=(item:any)=>{
    // Handle all terminology systems:
    // ICD10: { code, description, category }
    // SNOMED: { concept_id, description, semantic_tag }
    // LOINC: { loinc_num, long_common_name, component }
    const display=item.description||item.long_common_name||item.preferred_term||item.display||'';
    const code=item.code||item.loinc_num||item.concept_id||'';
    const label=display+(code&&showCode?` [${code}]`:'');
    setQuery(label);
    setSelected(item);
    setOpen(false);
    onChange(label,code,display);
  };

  const SYSTEM_COLORS:Record<string,string>={
    icd10:'bg-blue-100 text-[#02357A]',
    icd11:'bg-indigo-100 text-indigo-700',
    snomed:'bg-purple-100 text-purple-700',
    loinc:'bg-teal-100 text-teal-700',
  };

  return(
    <div className={`relative ${className}`} ref={ref}>
      {label&&<label className="label">{label}</label>}
      <div className="relative">
        <input

          className={`w-full text-sm px-3 py-1.5 border border-slate-200 rounded-lg outline-none focus:border-blue-400 ${selected?'border-green-400 bg-green-50':''}`}
          value={query}
          onChange={handleChange}
          onFocus={()=>{if(results.length) setOpen(true);}}
          placeholder={placeholder||`Search ${system==='icd11'?'ICD-11':system.toUpperCase()} codes...`}
        />
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {loading&&<span className="text-xs text-slate-400">⏳</span>}
          {selected?.code&&showCode&&(
            <span className={`text-xs px-1.5 py-0.5 rounded font-mono font-bold ${SYSTEM_COLORS[system]||'bg-slate-100 text-slate-600'}`}>
              {selected.code}
            </span>
          )}
          {query&&<button onClick={()=>{setQuery('');setSelected(null);setResults([]);setOpen(false);onChange('','','');}} className="text-slate-300 hover:text-slate-500 text-sm">×</button>}
        </div>
      </div>
      {open&&results.length>0&&(
        <div className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-xl shadow-xl max-h-60 overflow-y-auto">
          {results.map((item:any,i:number)=>{
            const display=item.description||item.long_common_name||item.preferred_term||item.display||'';
            const code=item.code||item.loinc_num||item.concept_id||'';
            return(
              <button key={i} type="button" onClick={()=>pick(item)}
                className="w-full text-left px-3 py-2.5 hover:bg-blue-50 flex items-start gap-2 text-sm border-b border-slate-50 last:border-0">
                {code&&<span className={`text-xs px-1.5 py-0.5 rounded font-mono font-bold flex-shrink-0 mt-0.5 ${SYSTEM_COLORS[system]||'bg-slate-100'}`}>{code}</span>}
                <span className="text-slate-700">{display}</span>
              </button>
            );
          })}
        </div>
      )}
      {selected?.code&&(
        <p className="text-xs text-green-600 mt-0.5">✓ {system.toUpperCase()} code: <span className="font-mono font-bold">{selected.code}</span></p>
      )}
    </div>
  );
}
